import qs from "qs";
import moment from "moment";
import { ethers } from "ethers";
import { useAppStore } from "@/plugins/2.pinia/modules/app.js";
import { ParticleBridge } from "@/utils/particleNetwork";
import { CHAIN_ID_MAP } from "@/utils/constant";

import { connect, disconnect } from "@particle-network/auth-core";

const BASE_URL = import.meta.env.VITE_BASE_URL || "";

// 👉 IsEmpty
export const isEmpty = (value) => {
  if (value === null || value === undefined || value === "") return true;

  return Array.isArray(value) && value.length === 0;
};

// 👉 IsNullOrUndefined
export const isNullOrUndefined = (value) => {
  return value === null || value === undefined;
};

// 👉 IsEmptyArray
export const isEmptyArray = (arr) => {
  return Array.isArray(arr) && arr.length === 0;
};

// 👉 IsObject
export const isObject = (obj) => {
  return obj !== null && typeof obj === "object" && !Array.isArray(obj);
};

// 👉 IsToday
export const isToday = (date) => {
  const today = new Date();

  return (
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()
  );
};

// 👉 IsHtml
export const isHtml = (str) => {
  if (typeof str !== "string") return false;
  const pattern = /<[^>]+>/;
  return pattern.test(str);
};

/**
 * Generate a random string.
 * @see https://dev.to/roblevintennis/comment/10148
 */
export const randomString = () => {
  return String(Date.now().toString(32) + Math.random().toString(16)).replace(
    /\./g,
    "",
  );
};

export const playSound = (audioUrl) => {
  const audio = new Audio(audioUrl);
  audio.play().catch((error) => {
    console.error("Error playing sound:", error);
  });
};

export const getApiPath = (path, params) => {
  return path.replace(/:([a-zA-Z0-9_-]+)/g, (_, key) =>
    key in params ? params[key] : _,
  );
};

export const truncateString = (str, maxLength = 9, suffix = "...") => {
  if (typeof str !== "string") return "";
  return str.length > maxLength ? `${str.slice(0, maxLength)}${suffix}` : str;
};

export function animateCounter(start, end, duration, callback) {
  const startTime = performance.now();

  function step(currentTime) {
    const elapsed = currentTime - startTime;
    const progress = Math.min(elapsed / duration, 1);
    const currentValue = Math.floor(
      start + (end - start) * easeOutCubic(progress),
    );

    callback(currentValue);

    if (progress < 1) {
      requestAnimationFrame(step);
    }
  }

  requestAnimationFrame(step);
}

function easeOutCubic(t) {
  return 1 - Math.pow(1 - t, 3);
}

export const resolveChainIdValue = (chainValue) => {
  if (chainValue === undefined || chainValue === null) return null;

  const normalized =
    typeof chainValue === "string" ? chainValue.trim() : chainValue;
  const mapped = CHAIN_ID_MAP[normalized];
  if (mapped) return mapped;

  const numericChain = Number(normalized);
  return Number.isFinite(numericChain) ? numericChain : null;
};

export const parseJwt = (token) => {
  const [header, payload, signature] = token.split(".");

  if (!header || !payload) {
    throw new Error("Invalid JWT token");
  }

  const decodedHeader = JSON.parse(base64UrlDecode(header));
  const decodedPayload = JSON.parse(base64UrlDecode(payload));

  return {
    header: decodedHeader,
    payload: decodedPayload,
    signature,
  };
};

export const stringToSlug = (str) => {
  return str
    .normalize("NFD") // Normalize to decompose accented characters
    .replace(/[\u0300-\u036f]/g, "") // Remove accents
    .toLowerCase() // Convert to lowercase
    .trim() // Trim whitespace from both sides
    .replace(/\s+/g, "-") // Replace spaces with hyphens
    .replace(/[^\w-]+/g, ""); // Remove all non-word chars
};

export const carousel1 = import.meta.glob(
  "/src/assets/images/carousel-1/*.png",
  {
    eager: true,
  },
);

export const carousel2 = import.meta.glob(
  "/src/assets/images/carousel-2/*.png",
  {
    eager: true,
  },
);

export const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export const srcAsset = (asset) => {
  return asset
    ? `${import.meta.env.VITE_ASSET_URL}/${asset}`
    : "https://via.placeholder.com/150";
};

export const base64urlDecode = (str) => {
  const base64 = str
    .replace(/-/g, "+")
    .replace(/_/g, "/")
    .padEnd(str.length + ((4 - (str.length % 4)) % 4), "=");
  return atob(base64);
};

// Import all images from the assets/images folder but only when needed
const imageFiles = import.meta.glob("@images/*", { eager: true });

/**
 * Get image list based on predefined filenames
 * @param {string[]} filenames - List of image filenames to load
 * @returns {Array<{ name: string, displayUrl: string, saveUrl: string }>}
 */
export const getImagesByNames = (filenames) => {
  return filenames
    .map((filename) => {
      const filePath = `/src/assets/images/${filename}`;
      const module = imageFiles[filePath]; // Check if the image exists
      return {
        name: filename,
        displayUrl: module ? module.default : "", // URL for displaying the image
        saveUrl: `${BASE_URL}/assets/images/${filename}`, // URL for saving the image
      };
    })
    .filter((image) => image.displayUrl); // Filter out images that do not exist
};

export const customFormatter = (val) => {
  if (!val) return null;
  const date = new Date(val);
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, "0");
  const day = date.getDate().toString().padStart(2, "0");
  console.log("date", `${day}/${month}/${year}`);
  return `${day}/${month}/${year}`;
};

export const getErrorMessages = (errors, id) => {
  const error = errors.find((err) => err.id === id);
  return error ? error.errorMessages : [];
};

export const thirdpartyLogin = async () => {
  try {
    const query = qs.parse(window.location.search, {
      ignoreQueryPrefix: true,
    });
    const particleThirdpartyParamsContent = query?.particleThirdpartyParams;
    if (!particleThirdpartyParamsContent) return;

    delete query.particleThirdpartyParams;
    const replaceUrl =
      `${window.location.origin}${window.location.pathname}` +
      (qs.stringify(query) ? `?${qs.stringify(query)}` : "");
    window.history.replaceState({}, document.title, replaceUrl);

    const particleThirdpartyParams = JSON.parse(
      base64urlDecode(particleThirdpartyParamsContent),
    );
    const { code, nonce, appState, error } = particleThirdpartyParams;
    if (error) {
      disconnect();
      return;
    }

    const authType = nonce.split("@")[0];
    await connect({ socialType: authType, code, nonce });
    return true;
  } catch (error) {
    console.error("Error during third-party login", error);
  }
};

/**
 * Select all text in an input or textarea field
 * @param {HTMLInputElement | HTMLTextAreaElement} target - The input element
 */
export const selectAllText = (target) => {
  if (!target) return;
  target.focus();
  target.select();
};

export const getEVMPublicAddress = () => {
  return window.particleAuth?.ethereum.selectedAddress || "";
};

export const convertImageToFile = async (imagePath, fileName = "image.png") => {
  const response = await fetch(imagePath);
  const blob = await response.blob();
  return new File([blob], fileName, { type: blob.type });
};

export const shortenAddress = (address, length = 6) => {
  if (!address || address.length <= length * 2) return address;
  return `${address.slice(0, length)}...${address.slice(-length)}`;
};

export const useTimeFormat = (timestamp) => {
  const now = moment();
  const target =
    String(timestamp).length === 10
      ? moment.unix(Number(timestamp)) // seconds
      : moment(Number(timestamp)); // milliseconds

  const duration = moment.duration(now.diff(target));

  const timeFormats = [
    { unit: "y", value: duration.asYears() },
    { unit: "w", value: duration.asWeeks() },
    { unit: "d", value: duration.asDays() },
    { unit: "h", value: duration.asHours() },
    { unit: "m", value: duration.asMinutes() },
    { unit: "s", value: duration.asSeconds() },
  ];

  const bestFit = timeFormats.find((time) => time.value >= 1) || {
    unit: "s",
    value: 0,
  };
  return `${Math.floor(bestFit.value)}${bestFit.unit}`;
};

export const formatNumber = (n) => {
  n = Number(n);
  if (n === 0) return "0";
  const abs = Math.abs(n);
  const sign = Math.sign(n);

  if (abs >= 1e12) return `${sign * (abs / 1e12).toFixed(1)}T`;
  if (abs >= 1e9) return `${sign * (abs / 1e9).toFixed(1)}B`;
  if (abs >= 1e6) return `${sign * (abs / 1e6).toFixed(1)}M`;
  if (abs >= 1e3) return `${sign * (abs / 1e3).toFixed(1)}K`;
  if (abs >= 1) return (sign * abs).toFixed(5).replace(/\.?0+$/, "");
  if (abs >= 1e-6) return (sign * abs).toFixed(5);
  return (sign * abs).toFixed(5);
};

export async function waitDialogConfirm(dialog) {
  return await new Promise((resolve) => {
    const stopWatch = watch(
      () => dialog.confirmDialog,
      async (newVal) => {
        if (!newVal) {
          resolve();
          stopWatch();
        }
      },
    );
  });
}

import AIRDROP_TRACKER from "@data/artifacts/TokenWarsFuji_AirdropTracker.json";
import BATTLE_AIRDROP from "@data/battleAirdropABI.json";
import BATTLE_POOL from "@data/battlePoolABI.json";
import BASE_MANAGER from "@data/artifacts/TokenWarsFuji_CataclysmBase.json";
import MAP_NFT from "@data/artifacts/TokenWarsFuji_CataclysmMap.json";
import GAME_MANAGER from "@data/artifacts/TokenWarsFuji_GameManager.json";
import GAME_MANAGER_REWARDS from "@data/artifacts/TokenWarsFuji_GameManagerRewards.json";
import MAP_SVG_GENERATOR from "@data/artifacts/TokenWarsFuji_MapSVGGenerator.json";
import NEXUS_TOKEN from "@data/artifacts/TokenWarsFuji_NexusToken.json";
import REWARD_PROCESSOR from "@data/artifacts/TokenWarsFuji_RewardProcessor.json";
import TOWER_CONTROLLER from "@data/artifacts/TokenWarsFuji_TowerController.json";
import VRUMBLERS from "@/abis/vrumbler_abi.json";
import SPARKLE from "@/abis/sparkle_abi.json";
import ANOINT from "@/abis/anoint_abi.json";
import CRYSTALIZE from "@/abis/crystalize_abi.json";
import MYSTIC_INFO from "@/abis/mysticinfo_abi.json";

export const AGGREGATOR_V3_ABI = [
  {
    inputs: [],
    name: "latestRoundData",
    outputs: [
      { name: "roundId", type: "uint80" },
      { name: "answer", type: "int256" },
      { name: "startedAt", type: "uint256" },
      { name: "updatedAt", type: "uint256" },
      { name: "answeredInRound", type: "uint80" },
    ],
    stateMutability: "view",
    type: "function",
  },
  {
    inputs: [],
    name: "decimals",
    outputs: [{ name: "", type: "uint8" }],
    stateMutability: "view",
    type: "function",
  },
];

const AVAX_USD_FEED = "0x0A77230d17318075983913bC2145DB16C7366156";
const DEFAULT_AVAX_RPC =
  import.meta.env.VITE_AVALANCHE_RPC || "https://api.avax.network/ext/bc/C/rpc";

export const abiMap = {
  AIRDROP_TRACKER: AIRDROP_TRACKER,
  BATTLE_AIRDROP: BATTLE_AIRDROP,
  BATTLE_POOL: BATTLE_POOL,
  BASE_MANAGER: BASE_MANAGER,
  MAP_NFT: MAP_NFT,
  GAME_MANAGER: GAME_MANAGER,
  GAME_MANAGER_REWARDS: GAME_MANAGER_REWARDS,
  MAP_SVG_GENERATOR: MAP_SVG_GENERATOR,
  NEXUS_TOKEN: NEXUS_TOKEN,
  REWARD_PROCESSOR: REWARD_PROCESSOR,
  TOWER_CONTROLLER: TOWER_CONTROLLER,
  VRUMBLERS: VRUMBLERS,
  SPARKLE: SPARKLE,
  ANOINT: ANOINT,
  CRYSTALIZE: CRYSTALIZE,
  MYSTIC_INFO: MYSTIC_INFO,
};

// Explicit public contract addresses avoid bundling the entire Vite environment.
const contractAddresses = {
  AIRDROP_TRACKER: import.meta.env.VITE_CONTRACT_AIRDROP_TRACKER,
  BATTLE_AIRDROP: import.meta.env.VITE_CONTRACT_BATTLE_AIRDROP,
  BATTLE_POOL: import.meta.env.VITE_CONTRACT_BATTLE_POOL,
  BASE_MANAGER: import.meta.env.VITE_CONTRACT_BASE_MANAGER,
  MAP_NFT: import.meta.env.VITE_CONTRACT_MAP_NFT,
  GAME_MANAGER: import.meta.env.VITE_CONTRACT_GAME_MANAGER,
  GAME_MANAGER_REWARDS: import.meta.env.VITE_CONTRACT_GAME_MANAGER_REWARDS,
  MAP_SVG_GENERATOR: import.meta.env.VITE_CONTRACT_MAP_SVG_GENERATOR,
  NEXUS_TOKEN: import.meta.env.VITE_CONTRACT_NEXUS_TOKEN,
  REWARD_PROCESSOR: import.meta.env.VITE_CONTRACT_REWARD_PROCESSOR,
  TOWER_CONTROLLER: import.meta.env.VITE_CONTRACT_TOWER_CONTROLLER,
  VRUMBLERS: import.meta.env.VITE_CONTRACT_VRUMBLERS,
  SPARKLE: import.meta.env.VITE_CONTRACT_SPARKLE,
  ANOINT: import.meta.env.VITE_CONTRACT_ANOINT,
  CRYSTALIZE: import.meta.env.VITE_CONTRACT_CRYSTALIZE,
  MYSTIC_INFO: import.meta.env.VITE_CONTRACT_MYSTIC_INFO,
};
const abiByAddress = Object.fromEntries(
  Object.entries(abiMap)
    .filter(([key]) => contractAddresses[key])
    .map(([key, abi]) => [contractAddresses[key].toLowerCase(), abi]),
);

export const getAbiByAddress = (address) => {
  const normalized = address.toLowerCase();
  const abi = abiByAddress[normalized];

  if (!abi) {
    throw new Error(`ABI not found for address: ${address}`);
  }

  if (Array.isArray(abi)) {
    return abi;
  }

  if (abi.abi && Array.isArray(abi.abi)) {
    return abi.abi;
  }

  throw new Error(`Invalid ABI format for address: ${address}`);
};

export const base64ToHex = (b64) => {
  const bin = atob(b64);
  return (
    "0x" +
    Array.from(bin)
      .map((c) => c.charCodeAt(0).toString(16).padStart(2, "0"))
      .join("")
  );
};

export const formatNumberValue = (number, decimals = 9) => {
  if (!Number.isFinite(number) || number === 0) {
    return "0";
  }

  return number
    .toFixed(decimals)
    .replace(/\.?0+$/, "")
    .replace(/\.$/, ".0");
};

const toAvaxNumber = (cost, isAvaxValue = false) => {
  try {
    if (!cost || cost === "0") return 0;
    if (isAvaxValue) {
      return typeof cost === "number" ? cost : parseFloat(cost) || 0;
    }
    if (
      typeof cost === "number" ||
      (typeof cost === "string" && cost.includes("."))
    ) {
      return parseFloat(cost) || 0;
    }
    return parseFloat(ethers.formatEther(cost));
  } catch (error) {
    console.error("Error parsing AVAX amount:", error);
    return 0;
  }
};

const AVAX_UNIT_CONFIG = {
  AVAX: { unit: "AVAX", factor: 1, decimals: 4 },
  mAVAX: { unit: "mAVAX", factor: 1_000, decimals: 2 },
};

const resolveAvaxUnit = (avaxValue, forceUnit) => {
  if (forceUnit && AVAX_UNIT_CONFIG[forceUnit]) {
    return AVAX_UNIT_CONFIG[forceUnit];
  }
  if (avaxValue > 0 && avaxValue < 1) {
    return AVAX_UNIT_CONFIG.mAVAX;
  }
  return AVAX_UNIT_CONFIG.AVAX;
};

export const fetchAvaxUsd = async (rpcUrl = DEFAULT_AVAX_RPC) => {
  const provider = new ethers.JsonRpcProvider(rpcUrl);
  const feed = new ethers.Contract(AVAX_USD_FEED, AGGREGATOR_V3_ABI, provider);
  const decimals = await feed.decimals();
  const { answer } = await feed.latestRoundData();
  return Number(answer) / 10 ** Number(decimals);
};

export const avaxToUsd = async (avaxAmount, rpcUrl) => {
  const rate = await fetchAvaxUsd(rpcUrl);
  return Number(avaxAmount) * rate;
};

export const formatEtherString = (cost, decimals = 9) => {
  try {
    const number = toAvaxNumber(cost, false);
    return formatNumberValue(number, decimals);
  } catch (error) {
    console.error("Error formatting ether:", error);
    return "0.0";
  }
};

export const formatAvaxDisplay = (cost, options = {}) => {
  const { isAvaxValue = false, forceUnit, decimalsByUnit = {} } = options || {};

  const avaxValue = toAvaxNumber(cost, isAvaxValue);
  const unitConfig = resolveAvaxUnit(avaxValue, forceUnit);
  const decimals = decimalsByUnit[unitConfig.unit] ?? unitConfig.decimals;
  const scaledValue = avaxValue * unitConfig.factor;
  const value = formatNumberValue(scaledValue, decimals);

  return {
    value,
    unit: unitConfig.unit,
    avax: avaxValue,
    multiplier: unitConfig.factor,
    decimals,
  };
};

export function extractTransactionHash(result) {
  if (!result) return null;
  if (typeof result === "string") return result;

  if (typeof result === "object") {
    return (
      result.transactionHash ||
      result.hash ||
      result.signature ||
      result.txHash ||
      result.result ||
      null
    );
  }

  return null;
}

export function resolveTransactionHash(...sources) {
  for (const source of sources) {
    const hash = extractTransactionHash(source);
    if (hash) {
      return hash;
    }
  }
  return "";
}

const defaultAlertTitleByType = {
  success: "TRANSACTION SUCCESS",
  warning: "TRANSACTION WARNING",
  info: "TRANSACTION UPDATE",
  error: "TRANSACTION FAILED",
};

const defaultAlertColorByType = {
  success: "success",
  warning: "warning",
  info: "info",
  error: "error",
};

const TX_ALERT_ERROR_PREFIX_RE = /^(error|execution|reason)\s*:/i;
const TX_ALERT_INSUFFICIENT_RE =
  /(insufficient funds|insufficient (?:avax|eth|matic|bnb|balance)|not enough funds|funds for gas|gas \* price \+ value|exceeds balance|INSUFFICIENT_FUNDS)/i;

function ensureErrorPrefix(message) {
  const trimmed = (message || "").trim();
  if (!trimmed) return "";
  if (TX_ALERT_ERROR_PREFIX_RE.test(trimmed)) return trimmed;
  return `Error: ${trimmed}`;
}

function normalizeTxBaseMessage(baseMessage, type) {
  const trimmed = (baseMessage || "").trim();
  if (!trimmed) return "";

  if (String(type || "").toLowerCase() !== "error") {
    return trimmed;
  }

  // Only auto-prefix simple single-line error messages. Multi-line messages
  // are assumed to already be formatted.
  if (trimmed.includes("\n")) return trimmed;
  if (TX_ALERT_ERROR_PREFIX_RE.test(trimmed)) return trimmed;

  return ensureErrorPrefix(trimmed);
}

function isInsufficientFundsError(message) {
  if (!message) return false;
  return TX_ALERT_INSUFFICIENT_RE.test(String(message));
}

export function notifyTransactionAlert(payload, options = {}) {
  if (!payload && !options.message) {
    return;
  }

  const {
    type: overrideType,
    title: overrideTitle,
    message: overrideMessage,
    color: overrideColor,
    timeout: overrideTimeout,
    txHash: overrideTxHash,
    copyText: overrideCopyText,
    disableDuplicateGuard = false,
    ...alertOverrides
  } = options || {};

  const duplicateGuardTarget =
    typeof payload === "object" && payload ? payload : undefined;

  if (
    duplicateGuardTarget &&
    !disableDuplicateGuard &&
    duplicateGuardTarget.__transactionAlerted
  ) {
    return;
  }

  const resolvedTypeRaw =
    overrideType ||
    (typeof payload === "object" && payload?.alertType) ||
    "error";
  const resolvedType =
    typeof resolvedTypeRaw === "string"
      ? resolvedTypeRaw.toLowerCase()
      : "error";

  const resolvedMessage =
    overrideMessage ||
    (typeof payload === "string"
      ? payload
      : typeof payload === "object" && payload
        ? payload.errorMessage ||
          payload.message ||
          payload.details ||
          payload.error?.message ||
          "Unknown error"
        : String(payload));

  const resolvedTitle =
    overrideTitle || defaultAlertTitleByType[resolvedType] || "";

  const prefixedMessage =
    resolvedType === "error" &&
    !overrideMessage &&
    typeof payload === "object" &&
    payload
      ? ensureErrorPrefix(resolvedMessage)
      : resolvedMessage;

  const alertMessage = resolvedTitle
    ? `${resolvedTitle}\n${prefixedMessage}`
    : prefixedMessage;

  const appStore = useAppStore();
  if (!appStore?.addAlert) {
    return;
  }

  const payloadTxHash =
    typeof payload === "object" && payload ? payload.txHash : undefined;
  const resolvedTxHash = overrideTxHash || payloadTxHash || undefined;
  const resolvedCopyText =
    overrideCopyText !== undefined
      ? overrideCopyText
      : resolvedTxHash || undefined;

  appStore.addAlert(alertMessage, {
    type: resolvedType,
    color:
      overrideColor ||
      defaultAlertColorByType[resolvedType] ||
      defaultAlertColorByType.info,
    timeout: overrideTimeout ?? 10000,
    txHash: resolvedTxHash,
    copyText: resolvedCopyText,
    ...alertOverrides,
  });

  if (duplicateGuardTarget && !disableDuplicateGuard) {
    Object.defineProperty(duplicateGuardTarget, "__transactionAlerted", {
      value: true,
      enumerable: false,
      configurable: true,
      writable: true,
    });
  }
}

export function dispatchTxAlert({
  action,
  summary,
  errorMessage,
  title,
  baseMessage,
  txHash,
  type = "success",
  timeout = 12000,
  ...rest
}) {
  const normalizedHash = txHash || "";

  const normalizedType = String(type || "").toLowerCase();
  const shouldStandardizeInsufficient =
    normalizedType === "error" &&
    !baseMessage &&
    isInsufficientFundsError(errorMessage);

  const resolvedBaseMessageRaw =
    baseMessage ||
    (() => {
      const lines = [];
      const actionLabel = typeof action === "string" ? action.trim() : "";
      const summaryText = shouldStandardizeInsufficient
        ? "Insufficient balance."
        : typeof summary === "string"
          ? summary.trim()
          : "";

      if (actionLabel && summaryText) {
        lines.push(`${actionLabel}: ${summaryText}`);
      } else if (summaryText) {
        lines.push(summaryText);
      } else if (actionLabel) {
        lines.push(actionLabel);
      } else if (title) {
        lines.push(title);
      }

      if (String(type || "").toLowerCase() === "error" && errorMessage) {
        // For tx alerts, the action line already provides context, so keep the
        // detail line clean (avoid repeating "Error:").
        lines.push(String(errorMessage).trim());
      }

      return lines.filter(Boolean).join("\n");
    })();

  const resolvedBaseMessage = normalizeTxBaseMessage(
    resolvedBaseMessageRaw,
    type,
  );

  notifyTransactionAlert(null, {
    type,
    title,
    message: normalizedHash
      ? `${resolvedBaseMessage}\nTx: ${shortenAddress(normalizedHash)}`
      : resolvedBaseMessage,
    txHash: normalizedHash || undefined,
    copyText: normalizedHash || undefined,
    timeout,
    ...rest,
  });
}

export async function sendParticleTransaction(
  txParams,
  waitForReceipt,
  options = {},
) {
  const { suppressFailureAlert = false } = options || {};

  if (!ParticleBridge || typeof ParticleBridge.sendTransaction !== "function") {
    const error = new Error("Wallet transaction service is not available");
    if (!suppressFailureAlert) {
      notifyTransactionAlert(error);
    }
    throw error;
  }

  console.log("Sending transaction via ParticleBridge:", txParams);

  try {
    const response = await ParticleBridge.sendTransaction(txParams);

    if (response && typeof response === "object" && response.blockNumber) {
      return response;
    }

    if (!waitForReceipt || typeof waitForReceipt !== "function") {
      return response;
    }

    const txHash = extractTransactionHash(response);

    if (!txHash) {
      throw new Error(
        "Unable to determine the wallet transaction hash",
      );
    }

    return await waitForReceipt(txHash);
  } catch (error) {
    if (!suppressFailureAlert) {
      notifyTransactionAlert(error);
    }
    throw error;
  }
}

const ERC20_MIN_ABI = [
  "function symbol() view returns (string)",
  "function decimals() view returns (uint8)",
  "function balanceOf(address) view returns (uint256)",
  "function allowance(address owner, address spender) view returns (uint256)",
  "function approve(address spender, uint256 amount) returns (bool)",
];

const toBigIntValue = (value) => {
  if (value === undefined || value === null) return 0n;
  if (typeof value === "bigint") return value;
  if (typeof value === "string" && value.length) return BigInt(value);
  if (typeof value === "number" && Number.isFinite(value)) return BigInt(value);
  return 0n;
};

export async function ensureNativeBalance({
  provider,
  from,
  tx,
  bufferBps = 2000, // 20% buffer
  minGasLimit = 21_000n,
  debugLabel = "ensureNativeBalance",
}) {
  if (!provider) throw new Error("Missing provider");
  if (!from) throw new Error("Missing from address");

  const [balanceWei, feeData] = await Promise.all([
    provider.getBalance(from).catch(() => 0n),
    provider.getFeeData().catch(() => null),
  ]);

  const gasPriceWei =
    feeData?.gasPrice ?? feeData?.maxFeePerGas ?? feeData?.lastBaseFeePerGas;

  let gasLimit = null;
  try {
    const estimatedGas = await provider.estimateGas({ ...(tx || {}), from });
    const buffered = (estimatedGas * (10_000n + BigInt(bufferBps))) / 10_000n;
    gasLimit = buffered > minGasLimit ? buffered : minGasLimit;
  } catch (error) {
    console.warn(`[${debugLabel}] estimateGas failed`, error);
    gasLimit = minGasLimit;
  }

  const valueWei = toBigIntValue(tx?.value);
  const gasCostWei =
    gasLimit && gasPriceWei ? gasLimit * BigInt(gasPriceWei) : 0n;
  const totalWei = valueWei + gasCostWei;

  console.log(`[${debugLabel}] preflight`, {
    balanceWei: balanceWei?.toString() ?? String(balanceWei),
    valueWei: valueWei?.toString() ?? String(valueWei),
    gasLimit: gasLimit?.toString() ?? null,
    gasPriceWei: gasPriceWei?.toString() ?? null,
    totalWei: totalWei?.toString() ?? String(totalWei),
  });

  if (BigInt(balanceWei ?? 0) < BigInt(totalWei)) {
    throw new Error("Insufficient AVAX balance for this transaction.");
  }
}

export async function ensureErc20Allowance({
  provider,
  from,
  tokenAddress,
  spender,
  amountWei,
  autoApprove = false,
  sendTransaction,
  estimateGas,
  debugLabel = "ensureErc20Allowance",
}) {
  if (!provider) throw new Error("Missing provider");
  if (!from) throw new Error("Missing from address");
  if (!tokenAddress) throw new Error("Missing token address");
  if (!spender) throw new Error("Missing spender address");

  const token = new ethers.Contract(tokenAddress, ERC20_MIN_ABI, provider);
  const [symbol, decimals, balance, allowance] = await Promise.all([
    token.symbol().catch(() => "TOKEN"),
    token.decimals().catch(() => 18),
    token.balanceOf(from).catch(() => 0n),
    token.allowance(from, spender).catch(() => 0n),
  ]);

  const needed = toBigIntValue(amountWei);

  console.log(`[${debugLabel}] preflight`, {
    symbol,
    decimals: Number(decimals ?? 18),
    balance: balance?.toString() ?? String(balance),
    allowance: allowance?.toString() ?? String(allowance),
    needed: needed?.toString() ?? String(needed),
  });

  if (BigInt(balance ?? 0) < BigInt(needed)) {
    throw new Error(`Insufficient ${symbol} balance`);
  }

  if (BigInt(allowance ?? 0) >= BigInt(needed)) {
    return { symbol, decimals, didApprove: false };
  }

  if (!autoApprove) {
    throw new Error(`Insufficient ${symbol} allowance`);
  }

  if (typeof sendTransaction !== "function") {
    throw new Error("sendTransaction is not available");
  }

  const approveIface = new ethers.Interface([
    "function approve(address spender, uint256 amount) returns (bool)",
  ]);
  const data = approveIface.encodeFunctionData("approve", [spender, needed]);

  const approveTx = {
    from,
    to: tokenAddress,
    data,
    value: "0x0",
  };

  const gasOverrides =
    typeof estimateGas === "function" ? await estimateGas(approveTx) : null;
  const approveTxWithGas = gasOverrides
    ? { ...approveTx, ...gasOverrides }
    : approveTx;

  console.log(`[${debugLabel}] approve tx`, approveTxWithGas);

  const approveResult = await sendTransaction(approveTxWithGas);
  const approveTxHash =
    (approveResult && typeof approveResult === "string" && approveResult) ||
    approveResult?.hash ||
    approveResult?.transactionHash ||
    "";

  return { symbol, decimals, didApprove: true, approveTxHash, approveResult };
}

export const fibonacci = (n) => {
  if (n <= 0) return 0;
  if (n === 1) return 1;

  let prev = 0;
  let curr = 1;

  for (let i = 2; i <= n; i += 1) {
    const next = prev + curr;
    prev = curr;
    curr = next;
  }

  return curr;
};
